import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
} from "@mui/material";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetCustomerListQuery, useDeleteCustomerMutation, useUpdateCustomerMutation } from "../../store/api/userAPI";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";
import { showToast, showError } from "../../constants/toasts";

const UserList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;
  const status = 2; // As per API requirement

  // Fetch customers from API
  const { data, isLoading, error, refetch } = useGetCustomerListQuery({
    status,
    page,
    limit,
  });

  // Delete customer mutation
  const [deleteCustomer, { isLoading: isDeleting }] = useDeleteCustomerMutation();
  
  // Update customer mutation (for toggle)
  const [updateCustomer, { isLoading: isUpdating }] = useUpdateCustomerMutation();

  // Extract customers from API response
  const customers = useMemo(() => {
    if (data?.data?.list && Array.isArray(data.data.list)) {
      return data.data.list.map((customer: any) => ({
        id: customer._id || customer.id,
        name: customer.fullName || "N/A",
        email: customer.email || "N/A",
        phone: customer.phone ? `${customer.countryCode || ""} ${customer.phone}` : "N/A",
        address: customer.address?.addressLine1 || "N/A",
        totalOrder: 0, // Not in API response
        loalityPoints: 0, // Not in API response
        image: customer.image || "/static/images/user_placeholder.png",
        isBlocked: customer.isBlocked || false,
      }));
    }
    return [];
  }, [data]);

  // Calculate total pages
  const totalCount = data?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / limit);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Delete confirmation modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<{ id: string; name: string } | null>(null);

  // Handle delete button click - opens confirmation modal
  const handleDeleteClick = (id: string, name: string) => {
    setCustomerToDelete({ id, name });
    setOpenDeleteModal(true);
  };

  // Handle delete confirmation - actually deletes the customer
  const handleConfirmDelete = async () => {
    if (!customerToDelete) return;

    try {
      const response = await deleteCustomer({ id: customerToDelete.id }).unwrap();
      
      // Show success message from API response or default message
      const successMessage = response?.message || `Customer "${customerToDelete.name}" deleted successfully`;
      showToast(successMessage);
      
      // Refetch the list after successful deletion
      refetch();
      // Close modal and reset state
      setOpenDeleteModal(false);
      setCustomerToDelete(null);
    } catch (error: any) {
      console.error("Error deleting customer:", error);
      
      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to delete customer. Please try again.";
      
      showError(errorMessage);
    }
  };

  // Handle delete modal close
  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setOpenDeleteModal(false);
      setCustomerToDelete(null);
    }
  };

  // Handle toggle change (enable/disable user)
  const handleToggleChange = async (userId: string, currentIsBlocked: boolean, userName: string) => {
    try {
      const newIsBlocked = !currentIsBlocked;
      
      const response = await updateCustomer({
        id: userId,
        isBlocked: newIsBlocked,
      }).unwrap();
      
      const successMessage = response?.message || 
        `User "${userName}" has been ${newIsBlocked ? "disabled" : "enabled"} successfully`;
      showToast(successMessage);
      
      // Refetch the list to update the UI
      refetch();
    } catch (error: any) {
      console.error("Error updating user status:", error);
      
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update user status. Please try again.";
      
      showError(errorMessage);
      
      // Refetch to revert the UI state
      refetch();
    }
  };

  return (
    <>
      <Loader isLoad={isLoading || isDeleting || isUpdating} />
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User Detail</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Total Order</TableCell>
              <TableCell>Loyality Points</TableCell>
              <TableCell>Enable / Disable</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {error ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Error loading users. Please try again.
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {isLoading ? "Loading..." : "No users found"}
                </TableCell>
              </TableRow>
            ) : (
              customers.map((user) => (
                <TableRow hover key={user.id}>
                  <TableCell className="click_action">
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/manage-users/detail", { state: { user } });
                      }}
                      className="user_block click_action"
                    >
                      <figure>
                        <img src={user.image} alt={user.name} />
                      </figure>
                      <p>
                        <b>{user.name}</b> <span>{user.phone}</span>{" "}
                        <span>{user.email}</span>
                      </p>
                    </a>
                  </TableCell>

                  <TableCell>{user.address}</TableCell>

                  <TableCell>{user.totalOrder}</TableCell>
                  <TableCell>
                    <span className="status_btn dark c_success">
                      {user.loalityPoints}
                    </span>
                  </TableCell>

                  <TableCell>
                    <SwitchToggle 
                      checked={!user.isBlocked} 
                      onChange={() => {
                        if (user.id) {
                          handleToggleChange(user.id, user.isBlocked, user.name);
                        }
                      }}
                      disabled={isUpdating || !user.id}
                    />
                  </TableCell>

                  <TableCell>
                    <div className="table_actions v2">
                      <IconButton
                        onClick={() =>
                          navigate("/manage-users/detail", { state: { user } })
                        }
                      >
                        <img src="/static/images/eye_icon.svg" alt="View" />
                      </IconButton>
                      <IconButton
                        onClick={() =>
                          navigate("/manage-users/edit", { state: { user } })
                        }
                      >
                        <img src="/static/images/edit2_icon.svg" alt="Edit" />
                      </IconButton>

                      <IconButton
                        onClick={() => user.id && handleDeleteClick(user.id, user.name)}
                        disabled={isDeleting || !user.id}
                      >
                        <img
                          src="/static/images/trash2_icon.svg"
                          alt="Delete"
                        />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 0 && (
        <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
          <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
            Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of <strong>{totalCount}</strong> entries
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Pagination
              module={customers}
              page={page}
              onPageChange={handlePageChange}
              totalPages={totalPages}
              setPage={setPage}
              totalCount={totalCount}
              limit={limit}
            />
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        setOpen={setOpenDeleteModal}
        onConfirm={handleConfirmDelete}
        itemName={customerToDelete?.name}
        isLoading={isDeleting}
      />
    </>
  );
};

export default UserList;
